import React, {useEffect, useMemo, useState} from "react";
import {getAllUsers} from "../services/UserService";
import {
    createEmployeeProfileForUser,
    getEmployeeProfileForUser,
    updateEmployeeProfileForUser,
} from "../services/EmployeeProfile";
import {
    createEmployeeProjectForUser,
    deleteEmployeeProject,
    getEmployeeProjectsForUser,
    updateEmployeeProject,
} from "../services/EmployeeProjects";
import {getCurrentUserRole} from "../utils/authHelper";
import {formatDate} from "../utils/dateConverter";
import {generateCvPdf, listCvFilesForUser} from "../services/CurriculumVitaeService";

import "../styles/EmployeeProfiles.css";

const emptySections = () => ({
    experience: [],
    education: [],
    certifications: [],
    languages: [],
    awards: [],
    publications: [],
    projects: [],
});

const parseSkillsText = (text) =>
    (text || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

const skillsToText = (skillsList) =>
    Array.isArray(skillsList) ? skillsList.join(", ") : "";

const ymOrDateToInput = (v) => {
    if (!v) return "";
    if (typeof v !== "string") return "";
    if (/^\d{4}-\d{2}$/.test(v)) return v;
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    return v;
};

const arrayToMultiline = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const parseMultiline = (text) =>
    (text || "")
        .split("\n")
        .map((x) => x.trim())
        .filter(Boolean);

const sectionTypeLabel = (t) => {
    switch (t) {
        case "experience":
            return "Experience";
        case "education":
            return "Education";
        case "certifications":
            return "Certifications";
        case "languages":
            return "Languages";
        case "awards":
            return "Awards";
        case "publications":
            return "Publications";
        case "projects":
            return "Projects (CV)";
        default:
            return t;
    }
};

export default function EmployeeProfiles() {
    const [role, setRole] = useState("");

    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState("");

    const [activeTab, setActiveTab] = useState("profile"); // profile | sections | projects

    const [profileLoaded, setProfileLoaded] = useState(false);
    const [profileExists, setProfileExists] = useState(false);

    const [profile, setProfile] = useState({
        title: "",
        summary: "",
        birthDate: "",
    });

    const [skills, setSkills] = useState([]);
    const [newSkill, setNewSkill] = useState("");

    const [sections, setSections] = useState(emptySections());
    const [newSectionType, setNewSectionType] = useState("experience");

    const [sectionsFilterType, setSectionsFilterType] = useState("experience");
    const [sectionsQuery, setSectionsQuery] = useState("");
    const [sectionsLimit, setSectionsLimit] = useState(5);

    const [projects, setProjects] = useState([]);
    const [newProject, setNewProject] = useState({
        title: "",
        startDate: "",
        endDate: "",
        skillsText: "",
        description: "",
        sortOrder: 0,
    });

    const [editId, setEditId] = useState(null);
    const [editProject, setEditProject] = useState({
        title: "",
        startDate: "",
        endDate: "",
        skillsText: "",
        description: "",
        sortOrder: 0,
    });

    const [cvFiles, setCvFiles] = useState([]);
    const [cvFilesLoading, setCvFilesLoading] = useState(false);

    const [cvGenerating, setCvGenerating] = useState(false);
    const [cvTitle, setCvTitle] = useState("");
    const [cvComment, setCvComment] = useState("");

    const loadAllForUser = async (userId) => {
        setProfileLoaded(false);
        await Promise.all([loadProfile(userId), loadProjects(userId), loadCvFiles(userId)]);
        setProfileLoaded(true);
    };

    const loadCvFiles = async (userId) => {
        setCvFilesLoading(true);
        try {
            const data = await listCvFilesForUser(userId);
            setCvFiles(Array.isArray(data?.files) ? data.files : []);
        } catch (err) {
            console.error("Failed to load CV files", err);
            setCvFiles([]);
        } finally {
            setCvFilesLoading(false);
        }
    };

    useEffect(() => {
        const r = getCurrentUserRole();
        setRole(r);

        if (r === "admin") void loadUsers();
    }, []);

    useEffect(() => {
        if (!selectedUserId) return;
        void loadAllForUser(selectedUserId);
    }, [selectedUserId]);

    const loadUsers = async () => {
        try {
            const u = await getAllUsers();
            setUsers(u);
            if (u.length > 0) setSelectedUserId(u[0].id);
        } catch (err) {
            console.error("Failed to load users", err);
            alert("Fehler beim Laden der Benutzer");
        }
    };

    const handleGenerateCv = async () => {
        if (!selectedUserId) return alert("Bitte Benutzer wählen");

        setCvGenerating(true);
        try {
            const title = (cvTitle || profile.title || "CV").trim();

            const {blob, filename} = await generateCvPdf(selectedUserId, {
                title,
                comment: cvComment || null,
            });

            // Download in browser
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            await loadCvFiles(selectedUserId);
        } catch (err) {
            console.error("Generate CV failed", err);
            alert("CV Generierung fehlgeschlagen");
        } finally {
            setCvGenerating(false);
        }
    };

    const loadProfile = async (userId) => {
        try {
            const p = await getEmployeeProfileForUser(userId);
            setProfileExists(true);

            setProfile({
                title: p.title ?? "",
                summary: p.summary ?? "",
                birthDate: p.birthDate ? p.birthDate.slice(0, 10) : "",
            });

            setSkills(Array.isArray(p.skills) ? p.skills : []);

            const s = p.sections ?? {};
            const nextSections = {
                ...emptySections(),
                ...s,
                experience: Array.isArray(s.experience) ? s.experience : [],
                education: Array.isArray(s.education) ? s.education : [],
                certifications: Array.isArray(s.certifications) ? s.certifications : [],
                languages: Array.isArray(s.languages) ? s.languages : [],
                awards: Array.isArray(s.awards) ? s.awards : [],
                publications: Array.isArray(s.publications) ? s.publications : [],
                projects: Array.isArray(s.projects) ? s.projects : [],
            };
            setSections(nextSections);

        } catch (err) {
            setProfileExists(false);
            setProfile({
                title: "",
                summary: "",
                birthDate: "",
            });
            setSkills([]);
            setSections(emptySections());
        }
    };

    const loadProjects = async (userId) => {
        try {
            const data = await getEmployeeProjectsForUser(userId);
            setProjects(data);
        } catch (err) {
            console.error("Failed to load projects", err);
            setProjects([]);
        }
    };


    const normalizeSkill = (s) => (s || "").trim();

    const addSkill = (skillRaw) => {
        const skill = normalizeSkill(skillRaw);
        if (!skill) return;

        setSkills((prev) => {
            const exists = prev.some((x) => x.toLowerCase() === skill.toLowerCase());
            if (exists) return prev;
            return [...prev, skill];
        });
        setNewSkill("");
    };

    const removeSkill = (skillToRemove) => {
        setSkills((prev) => prev.filter((s) => s !== skillToRemove));
    };

    const onSkillKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addSkill(newSkill);
        }
        if (e.key === "Backspace" && !newSkill && skills.length > 0) {
            removeSkill(skills[skills.length - 1]);
        }
    };

    const addSectionItem = () => {
        setSections((prev) => {
            const next = {...prev};

            switch (newSectionType) {
                case "experience":
                    next.experience = [
                        ...(prev.experience || []),
                        {
                            company: "",
                            position: "",
                            location: "",
                            startDate: "",
                            endDate: "",
                            current: false,
                            responsibilities: [],
                            achievements: [],
                        },
                    ];
                    break;
                case "education":
                    next.education = [
                        ...(prev.education || []),
                        {
                            degree: "",
                            field: "",
                            institution: "",
                            location: "",
                            startDate: "",
                            endDate: "",
                            grade: "",
                            thesis: "",
                        },
                    ];
                    break;
                case "certifications":
                    next.certifications = [
                        ...(prev.certifications || []),
                        {date: "", name: "", issuer: "", credentialId: ""},
                    ];
                    break;
                case "languages":
                    next.languages = [
                        ...(prev.languages || []),
                        {language: "", proficiency: ""},
                    ];
                    break;
                case "awards":
                    next.awards = [
                        ...(prev.awards || []),
                        {date: "", title: "", issuer: "", description: ""},
                    ];
                    break;
                case "publications":
                    next.publications = [
                        ...(prev.publications || []),
                        {date: "", title: "", publisher: "", coAuthors: []},
                    ];
                    break;
                case "projects":
                    next.projects = [
                        ...(prev.projects || []),
                        {
                            name: "",
                            role: "",
                            duration: "",
                            description: "",
                            achievements: [],
                            technologies: [],
                        },
                    ];
                    break;
                default:
                    break;
            }

            return next;
        });

        setSectionsFilterType(newSectionType);
    };

    const removeSectionItem = (type, index) => {
        setSections((prev) => {
            const arr = Array.isArray(prev[type]) ? [...prev[type]] : [];
            arr.splice(index, 1);
            return {...prev, [type]: arr};
        });
    };

    const updateSectionField = (type, index, field, value) => {
        setSections((prev) => {
            const arr = Array.isArray(prev[type]) ? [...prev[type]] : [];
            const item = {...(arr[index] || {})};
            item[field] = value;
            arr[index] = item;
            return {...prev, [type]: arr};
        });
    };

    const updateSectionArrayText = (type, index, field, text) => {
        const arr = parseMultiline(text);
        updateSectionField(type, index, field, arr);
    };

    const handleSaveProfile = async () => {
        if (!selectedUserId) return alert("Bitte Benutzer wählen");

        const dto = {
            title: profile.title || null,
            summary: profile.summary || null,
            birthDate: profile.birthDate || null,
            skills: skills,
            sections: sections,
        };

        try {
            if (profileExists) {
                await updateEmployeeProfileForUser(selectedUserId, dto);
            } else {
                await createEmployeeProfileForUser(selectedUserId, dto);
                setProfileExists(true);
            }
            await loadProfile(selectedUserId);
        } catch (err) {
            console.error("Save profile failed", err);
            alert("Fehler beim Speichern des Profils");
        }
    };

    const handleCreateProject = async () => {
        if (!selectedUserId) return alert("Bitte Benutzer wählen");
        if (!newProject.title) return alert("Bitte Titel eingeben");

        try {
            await createEmployeeProjectForUser(selectedUserId, {
                title: newProject.title,
                startDate: newProject.startDate || null,
                endDate: newProject.endDate || null,
                skills: parseSkillsText(newProject.skillsText),
                description: newProject.description || null,
                sortOrder: Number(newProject.sortOrder || 0),
            });

            setNewProject({
                title: "",
                startDate: "",
                endDate: "",
                skillsText: "",
                description: "",
                sortOrder: 0,
            });

            await loadProjects(selectedUserId);
        } catch (err) {
            console.error("Create project failed", err);
            alert("Fehler beim Anlegen des Projekts");
        }
    };

    const startEdit = (p) => {
        setEditId(p.id);
        setEditProject({
            title: p.title || "",
            startDate: p.startDate ? p.startDate.slice(0, 10) : "",
            endDate: p.endDate ? p.endDate.slice(0, 10) : "",
            skillsText: skillsToText(p.skills),
            description: p.description || "",
            sortOrder: p.sortOrder ?? 0,
        });
    };

    const cancelEdit = () => {
        setEditId(null);
        setEditProject({
            title: "",
            startDate: "",
            endDate: "",
            skillsText: "",
            description: "",
            sortOrder: 0,
        });
    };

    const saveEdit = async () => {
        if (!editId) return;
        if (!editProject.title) return alert("Bitte Titel eingeben");

        try {
            console.log(editProject);
            console.log(editId);
            await updateEmployeeProject(editId, {
                Title: editProject.title,
                StartDate: editProject.startDate || null,
                EndDate: editProject.endDate || null,
                Skills: parseSkillsText(editProject.skillsText),
                Description: editProject.description || null,
                SortOrder: Number(editProject.sortOrder || 0),
            });

            cancelEdit();
            await loadProjects(selectedUserId);
        } catch (err) {
            console.error("Update project failed", err);
            alert("Fehler beim Speichern");
        }
    };

    const handleDeleteProject = async (id) => {
        if (!window.confirm("Projekt wirklich löschen?")) return;

        try {
            await deleteEmployeeProject(id);
            await loadProjects(selectedUserId);
        } catch (err) {
            console.error("Delete project failed", err);
            alert("Fehler beim Löschen");
        }
    };

    const currentSectionItems = useMemo(() => {
        const arr = sections?.[sectionsFilterType];
        return Array.isArray(arr) ? arr : [];
    }, [sections, sectionsFilterType]);

    const filteredSectionItems = useMemo(() => {
        const q = (sectionsQuery || "").trim().toLowerCase();
        if (!q) return currentSectionItems;

        return currentSectionItems.filter((item) => {
            try {
                const text = JSON.stringify(item ?? {}).toLowerCase();
                return text.includes(q);
            } catch {
                return false;
            }
        });
    }, [currentSectionItems, sectionsQuery]);

    const limitedSectionItems = useMemo(() => {
        const n = Number(sectionsLimit || 0);
        if (!n || n <= 0) return filteredSectionItems;
        return filteredSectionItems.slice(0, n);
    }, [filteredSectionItems, sectionsLimit]);

    const renderFilteredHeader = () => {
        const total = currentSectionItems.length;
        const afterSearch = filteredSectionItems.length;
        const shown = limitedSectionItems.length;

        return (
            <div style={{display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12}}>

                <span style={{color: "#666"}}>
                    total: {total} | match: {afterSearch} | shown: {shown}
                </span>
            </div>
        );
    };

    const renderExperienceItem = (item, idx) => (
        <div key={idx} className="admin-section" style={{marginTop: 10}}>
            <div className="table-toolbar" style={{marginBottom: 8}}>
                <b>Eintrag #{idx + 1}</b>
                <button
                    type="button"
                    className="button button-danger"
                    onClick={() => removeSectionItem("experience", idx)}
                >
                    Entfernen
                </button>
            </div>

            <div className="two-col-grid">
                <div className="two-col-left">
                    <label>Company</label>
                    <input
                        className="input"
                        value={item.company || ""}
                        onChange={(e) => updateSectionField("experience", idx, "company", e.target.value)}
                    />

                    <label>Position</label>
                    <input
                        className="input"
                        value={item.position || ""}
                        onChange={(e) => updateSectionField("experience", idx, "position", e.target.value)}
                    />

                    <label>Location</label>
                    <input
                        className="input"
                        value={item.location || ""}
                        onChange={(e) => updateSectionField("experience", idx, "location", e.target.value)}
                    />

                    <label>Start (YYYY-MM)</label>
                    <input
                        type="month"
                        className="input"
                        value={ymOrDateToInput(item.startDate)}
                        onChange={(e) => updateSectionField("experience", idx, "startDate", e.target.value)}
                    />

                    <label>End (YYYY-MM)</label>
                    <input
                        type="month"
                        className="input"
                        disabled={!!item.current}
                        value={ymOrDateToInput(item.endDate)}
                        onChange={(e) => updateSectionField("experience", idx, "endDate", e.target.value)}
                    />

                    <label style={{display: "flex", alignItems: "center", gap: 8, marginTop: 8}}>
                        <input
                            type="checkbox"
                            checked={!!item.current}
                            onChange={(e) => {
                                updateSectionField("experience", idx, "current", e.target.checked);
                                if (e.target.checked) updateSectionField("experience", idx, "endDate", null);
                            }}
                        />
                        Current
                    </label>
                </div>

                <div className="two-col-right">
                    <label>Responsibilities (1 pro Zeile)</label>
                    <textarea
                        className="input"
                        style={{minHeight: 120}}
                        value={arrayToMultiline(item.responsibilities)}
                        onChange={(e) =>
                            updateSectionArrayText("experience", idx, "responsibilities", e.target.value)
                        }
                    />

                    <label>Achievements (1 pro Zeile)</label>
                    <textarea
                        className="input"
                        style={{minHeight: 120}}
                        value={arrayToMultiline(item.achievements)}
                        onChange={(e) =>
                            updateSectionArrayText("experience", idx, "achievements", e.target.value)
                        }
                    />
                </div>
            </div>
        </div>
    );

    const renderEducationItem = (item, idx) => (
        <div key={idx} className="admin-section" style={{marginTop: 10}}>
            <div className="table-toolbar" style={{marginBottom: 8}}>
                <b>Eintrag #{idx + 1}</b>
                <button
                    type="button"
                    className="button button-danger"
                    onClick={() => removeSectionItem("education", idx)}
                >
                    Entfernen
                </button>
            </div>

            <div className="two-col-grid">
                <div className="two-col-left">
                    <label>Degree</label>
                    <input
                        className="input"
                        value={item.degree || ""}
                        onChange={(e) => updateSectionField("education", idx, "degree", e.target.value)}
                    />

                    <label>Field</label>
                    <input
                        className="input"
                        value={item.field || ""}
                        onChange={(e) => updateSectionField("education", idx, "field", e.target.value)}
                    />

                    <label>Institution</label>
                    <input
                        className="input"
                        value={item.institution || ""}
                        onChange={(e) => updateSectionField("education", idx, "institution", e.target.value)}
                    />

                    <label>Location</label>
                    <input
                        className="input"
                        value={item.location || ""}
                        onChange={(e) => updateSectionField("education", idx, "location", e.target.value)}
                    />
                </div>

                <div className="two-col-right">
                    <label>Start (YYYY-MM)</label>
                    <input
                        type="month"
                        className="input"
                        value={ymOrDateToInput(item.startDate)}
                        onChange={(e) => updateSectionField("education", idx, "startDate", e.target.value)}
                    />

                    <label>End (YYYY-MM)</label>
                    <input
                        type="month"
                        className="input"
                        value={ymOrDateToInput(item.endDate)}
                        onChange={(e) => updateSectionField("education", idx, "endDate", e.target.value)}
                    />

                    <label>Grade</label>
                    <input
                        className="input"
                        value={item.grade || ""}
                        onChange={(e) => updateSectionField("education", idx, "grade", e.target.value)}
                    />

                    <label>Thesis</label>
                    <textarea
                        className="input"
                        style={{minHeight: 90}}
                        value={item.thesis || ""}
                        onChange={(e) => updateSectionField("education", idx, "thesis", e.target.value)}
                    />
                </div>
            </div>
        </div>
    );

    const renderCertificationItem = (item, idx) => (
        <div key={idx} className="admin-section" style={{marginTop: 10}}>
            <div className="table-toolbar" style={{marginBottom: 8}}>
                <b>Eintrag #{idx + 1}</b>
                <button
                    type="button"
                    className="button button-danger"
                    onClick={() => removeSectionItem("certifications", idx)}
                >
                    Entfernen
                </button>
            </div>

            <label>Date (YYYY-MM)</label>
            <input
                type="month"
                className="input"
                value={ymOrDateToInput(item.date)}
                onChange={(e) => updateSectionField("certifications", idx, "date", e.target.value)}
            />

            <label>Name</label>
            <input
                className="input"
                value={item.name || ""}
                onChange={(e) => updateSectionField("certifications", idx, "name", e.target.value)}
            />

            <label>Issuer</label>
            <input
                className="input"
                value={item.issuer || ""}
                onChange={(e) => updateSectionField("certifications", idx, "issuer", e.target.value)}
            />

            <label>Credential ID</label>
            <input
                className="input"
                value={item.credentialId || ""}
                onChange={(e) => updateSectionField("certifications", idx, "credentialId", e.target.value)}
            />
        </div>
    );

    const renderLanguageItem = (item, idx) => (
        <div key={idx} className="admin-section" style={{marginTop: 10}}>
            <div className="table-toolbar" style={{marginBottom: 8}}>
                <b>Eintrag #{idx + 1}</b>
                <button
                    type="button"
                    className="button button-danger"
                    onClick={() => removeSectionItem("languages", idx)}
                >
                    Entfernen
                </button>
            </div>

            <label>Language</label>
            <input
                className="input"
                value={item.language || ""}
                onChange={(e) => updateSectionField("languages", idx, "language", e.target.value)}
            />

            <label>Proficiency</label>
            <input
                className="input"
                value={item.proficiency || ""}
                onChange={(e) => updateSectionField("languages", idx, "proficiency", e.target.value)}
            />
        </div>
    );

    const renderAwardItem = (item, idx) => (
        <div key={idx} className="admin-section" style={{marginTop: 10}}>
            <div className="table-toolbar" style={{marginBottom: 8}}>
                <b>Eintrag #{idx + 1}</b>
                <button
                    type="button"
                    className="button button-danger"
                    onClick={() => removeSectionItem("awards", idx)}
                >
                    Entfernen
                </button>
            </div>

            <label>Date (YYYY-MM)</label>
            <input
                type="month"
                className="input"
                value={ymOrDateToInput(item.date)}
                onChange={(e) => updateSectionField("awards", idx, "date", e.target.value)}
            />

            <label>Title</label>
            <input
                className="input"
                value={item.title || ""}
                onChange={(e) => updateSectionField("awards", idx, "title", e.target.value)}
            />

            <label>Issuer</label>
            <input
                className="input"
                value={item.issuer || ""}
                onChange={(e) => updateSectionField("awards", idx, "issuer", e.target.value)}
            />

            <label>Description</label>
            <textarea
                className="input"
                style={{minHeight: 90}}
                value={item.description || ""}
                onChange={(e) => updateSectionField("awards", idx, "description", e.target.value)}
            />
        </div>
    );

    const renderPublicationItem = (item, idx) => (
        <div key={idx} className="admin-section" style={{marginTop: 10}}>
            <div className="table-toolbar" style={{marginBottom: 8}}>
                <b>Eintrag #{idx + 1}</b>
                <button
                    type="button"
                    className="button button-danger"
                    onClick={() => removeSectionItem("publications", idx)}
                >
                    Entfernen
                </button>
            </div>

            <label>Date (YYYY-MM)</label>
            <input
                type="month"
                className="input"
                value={ymOrDateToInput(item.date)}
                onChange={(e) => updateSectionField("publications", idx, "date", e.target.value)}
            />

            <label>Title</label>
            <input
                className="input"
                value={item.title || ""}
                onChange={(e) => updateSectionField("publications", idx, "title", e.target.value)}
            />

            <label>Publisher</label>
            <input
                className="input"
                value={item.publisher || ""}
                onChange={(e) => updateSectionField("publications", idx, "publisher", e.target.value)}
            />

            <label>Co-Authors (1 pro Zeile)</label>
            <textarea
                className="input"
                style={{minHeight: 90}}
                value={arrayToMultiline(item.coAuthors)}
                onChange={(e) => updateSectionArrayText("publications", idx, "coAuthors", e.target.value)}
            />
        </div>
    );


    const renderFilteredSectionItems = () => {
        if (!profileLoaded) return <p>Lade...</p>;

        if (limitedSectionItems.length === 0) {
            return <p style={{color: "#666"}}>Keine Einträge gefunden.</p>;
        }

        const q = (sectionsQuery || "").trim().toLowerCase();
        const matchingIndices = currentSectionItems
            .map((item, i) => {
                if (!q) return i;
                try {
                    return JSON.stringify(item ?? {}).toLowerCase().includes(q) ? i : -1;
                } catch {
                    return -1;
                }
            })
            .filter((i) => i >= 0)
            .slice(0, Number(sectionsLimit || 0) > 0 ? Number(sectionsLimit) : undefined);

        return (
            <div>
                {matchingIndices.map((originalIndex) => {
                    const item = currentSectionItems[originalIndex] || {};
                    switch (sectionsFilterType) {
                        case "experience":
                            return renderExperienceItem(item, originalIndex);
                        case "education":
                            return renderEducationItem(item, originalIndex);
                        case "certifications":
                            return renderCertificationItem(item, originalIndex);
                        case "languages":
                            return renderLanguageItem(item, originalIndex);
                        case "awards":
                            return renderAwardItem(item, originalIndex);
                        case "publications":
                            return renderPublicationItem(item, originalIndex);
                        default:
                            return null;
                    }
                })}
            </div>
        );
    };

    if (role !== "admin") {
        return (
            <div className="employee-profile-container">
                <h2>Mitarbeiter CV</h2>
                <div className="admin-section">
                    <p>Kein Zugriff.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="employee-profile-container">
            <h2>Mitarbeiter CV</h2>

            {/* user select */}
            <div style={{marginBottom: 16}}>
                <label>Mitarbeiter</label>
                <select
                    className="input"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}>
                    {users.map((u) => (
                        <option key={u.id} value={u.id}>
                            {u.username} ({u.name})
                        </option>
                    ))}
                </select>
            </div>

            <div className="internal-nav">
                <button
                    type="button"
                    className={`internal-tab ${activeTab === "profile" ? "active" : ""}`}
                    onClick={() => setActiveTab("profile")}
                >
                    Grunddaten
                </button>
                <button
                    type="button"
                    className={`internal-tab ${activeTab === "sections" ? "active" : ""}`}
                    onClick={() => setActiveTab("sections")}
                >
                    Sections
                </button>
                <button
                    type="button"
                    className={`internal-tab ${activeTab === "projects" ? "active" : ""}`}
                    onClick={() => setActiveTab("projects")}
                >
                    Projekte (Tabelle)
                </button>
                <button
                    type="button"
                    className={`internal-tab ${activeTab === "cvFiles" ? "active" : ""}`}
                    onClick={() => setActiveTab("cvFiles")}
                >
                    CV Dateien
                </button>
            </div>


            {activeTab === "cvFiles" && (
                <div>
                    <h3>CV Dateien / PDF erzeugen</h3>

                    {!selectedUserId ? (
                        <p style={{color: "#666"}}>Bitte zuerst einen Mitarbeiter auswählen.</p>
                    ) : (
                        <>
                            <div className="admin-section" style={{marginTop: 10}}>
                                <h4>PDF generieren</h4>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: 10,
                                        flexWrap: "wrap",
                                        alignItems: "end",
                                        marginTop: 8,
                                    }}
                                >
                                    <div style={{minWidth: 240}}>
                                        <label>Titel (für Dateiname)</label>
                                        <input
                                            className="input"
                                            value={cvTitle}
                                            onChange={(e) => setCvTitle(e.target.value)}
                                            placeholder="z.B. Senior_Engineer_CV"
                                        />
                                    </div>

                                    <div style={{flex: 1, minWidth: 260}}>
                                        <label>Kommentar (optional)</label>
                                        <input
                                            className="input"
                                            value={cvComment}
                                            onChange={(e) => setCvComment(e.target.value)}
                                            placeholder="z.B. Version für Kunde X"
                                        />
                                    </div>

                                    <button
                                        type="button"
                                        className="button"
                                        onClick={handleGenerateCv}
                                        disabled={!profileLoaded || cvGenerating}
                                        style={{whiteSpace: "nowrap"}}
                                    >
                                        {cvGenerating ? "Erstelle PDF..." : "CV generieren & herunterladen"}
                                    </button>
                                </div>
                            </div>

                            <div className="admin-section" style={{marginTop: 12}}>
                                <div
                                    className="table-toolbar"
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "end",
                                        gap: 12
                                    }}>
                                    <div>
                                        <h4 style={{margin: 0}}>Verfügbare S3</h4>
                                        <span
                                            style={{color: "#666"}}>{cvFilesLoading ? "Lade..." : `${cvFiles.length} Datei(en)`}
                                        </span>
                                    </div>

                                    <button
                                        type="button"
                                        className="button"
                                        onClick={() => loadCvFiles(selectedUserId)}
                                        disabled={cvFilesLoading}
                                        style={{whiteSpace: "nowrap"}}
                                    >
                                        {cvFilesLoading ? "Lade..." : "Liste aktualisieren"}
                                    </button>
                                </div>

                                <div style={{marginTop: 10}}>
                                    {cvFilesLoading ? (
                                        <p>Lade Dateien...</p>
                                    ) : cvFiles.length === 0 ? (
                                        <p style={{color: "#666"}}>Keine Dateien gefunden.</p>
                                    ) : (
                                        <table className="table">
                                            <thead>
                                            <tr>
                                                <th>Datei</th>
                                                <th>Größe</th>
                                                <th>Zuletzt geändert</th>
                                                <th>Aktion</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {cvFiles.map((f) => (
                                                <tr key={f.key}>
                                                    <td>{f.fileName || f.key}</td>
                                                    <td>{typeof f.size === "number" ? `${Math.round(f.size / 1024)} KB` : "-"}</td>
                                                    <td>{f.lastModified ? new Date(f.lastModified).toLocaleString() : "-"}</td>
                                                    <td>
                                                        <a href={f.url} target="_blank" rel="noreferrer">
                                                            Öffnen
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* PROFILE TAB */}
            {activeTab === "profile" && (
                <div>
                    <h3>Profil bearbeiten:</h3>

                    {!profileLoaded ? (
                        <p>Lade...</p>
                    ) : (
                        <div className="two-col-grid">
                            <div className="two-col-left">
                                <h4>Stammdaten</h4>

                                <label>Titel</label>
                                <input
                                    className="input"
                                    value={profile.title}
                                    onChange={(e) => setProfile((p) => ({...p, title: e.target.value}))}
                                />

                                <label>Geburtsdatum</label>
                                <input
                                    type="date"
                                    className="input"
                                    value={profile.birthDate}
                                    onChange={(e) => setProfile((p) => ({...p, birthDate: e.target.value}))}
                                />

                                <button type="button" onClick={handleSaveProfile} className="button"
                                        style={{marginTop: 8}}>
                                    {profileExists ? "Profil speichern" : "Profil anlegen"}
                                </button>
                            </div>

                            <div className="two-col-right">
                                <h4>Zusammenfassung</h4>
                                <textarea
                                    className="input"
                                    value={profile.summary}
                                    onChange={(e) => setProfile((p) => ({...p, summary: e.target.value}))}
                                    style={{minHeight: 240}}
                                    placeholder="Kurzprofil / Zusammenfassung…"
                                />

                                <h4 style={{marginTop: 16}}>Skills</h4>

                                <div style={{display: "flex", gap: 8, alignItems: "center"}}>
                                    <input
                                        className="input"
                                        style={{margin: 0}}
                                        value={newSkill}
                                        onChange={(e) => setNewSkill(e.target.value)}
                                        onKeyDown={onSkillKeyDown}
                                        placeholder="Skill eingeben und Enter drücken..."
                                    />
                                    <button
                                        type="button"
                                        className="button"
                                        onClick={() => addSkill(newSkill)}
                                        style={{whiteSpace: "nowrap"}}
                                    >
                                        Hinzufügen
                                    </button>
                                </div>

                                <div style={{marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap"}}>
                                    {skills.map((s) => (
                                        <div key={s} className="skill-tag">
                                            <span className="skill-tag-text">{s}</span>
                                            <button
                                                type="button"
                                                className="skill-tag-remove"
                                                onClick={() => removeSkill(s)}
                                                aria-label={`Remove skill ${s}`}
                                                title="Entfernen"
                                            >
                                                x
                                            </button>
                                        </div>
                                    ))}
                                    {skills.length === 0 && (
                                        <span style={{color: "#666"}}>Noch keine Skills.</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* SECTIONS TAB */}
            {activeTab === "sections" && (
                <div>
                    <h3>Sections bearbeiten:</h3>

                    {/* Toolbar: Add */}
                    <div className="table-toolbar">
                        <div>
                            <label>Neue Section hinzufügen:</label>
                            <select
                                className="input"
                                value={newSectionType}
                                onChange={(e) => setNewSectionType(e.target.value)}
                                style={{width: 240}}
                            >
                                <option value="experience">Experience</option>
                                <option value="education">Education</option>
                                <option value="certifications">Certifications</option>
                                <option value="languages">Languages</option>
                                <option value="awards">Awards</option>
                                <option value="publications">Publications</option>
                            </select>
                        </div>

                        <button
                            type="button"
                            className="button"
                            onClick={addSectionItem}
                            style={{alignSelf: "end"}}
                        >
                            Hinzufügen
                        </button>

                        <button
                            type="button"
                            className="button"
                            onClick={handleSaveProfile}
                            style={{alignSelf: "end"}}
                        >
                            Alles speichern
                        </button>
                    </div>

                    {/* Toolbar: Filter */}
                    <div className="table-toolbar" style={{marginTop: 8}}>
                        <div style={{minWidth: 260}}>
                            <label>Anzeige filtern (Section-Typ):</label>
                            <select
                                className="input"
                                value={sectionsFilterType}
                                onChange={(e) => setSectionsFilterType(e.target.value)}
                                style={{width: 240}}
                            >
                                <option value="experience">Experience</option>
                                <option value="education">Education</option>
                                <option value="certifications">Certifications</option>
                                <option value="languages">Languages</option>
                                <option value="awards">Awards</option>
                                <option value="publications">Publications</option>
                            </select>
                        </div>

                        <div style={{flex: 1, minWidth: 280}}>
                            <label>Suche:</label>
                            <input
                                className="input"
                                value={sectionsQuery}
                                onChange={(e) => setSectionsQuery(e.target.value)}
                                placeholder="z.B. KPI, Stuttgart, Azure, Master…"
                            />
                        </div>

                        <div style={{minWidth: 180}}>
                            <label>Limit:</label>
                            <select
                                className="input"
                                value={sectionsLimit}
                                onChange={(e) => setSectionsLimit(Number(e.target.value))}
                                style={{width: 160}}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={0}>Alle</option>
                            </select>
                        </div>
                    </div>

                    {renderFilteredHeader()}
                    {renderFilteredSectionItems()}
                </div>
            )}

            {/* PROJECTS TAB */}
            {activeTab === "projects" && (
                <div>
                    <h3>Projekte verwalten:</h3>

                    <div className="two-col-grid">
                        <div className="two-col-left">
                            <h4>Projekt anlegen</h4>

                            <label>Titel</label>
                            <input
                                className="input"
                                value={newProject.title}
                                onChange={(e) => setNewProject((p) => ({...p, title: e.target.value}))}
                                placeholder="z.B. CRM-System"
                            />

                            <label>Start</label>
                            <input
                                type="date"
                                className="input"
                                value={newProject.startDate}
                                onChange={(e) => setNewProject((p) => ({...p, startDate: e.target.value}))}
                            />

                            <label>Ende (optional)</label>
                            <input
                                type="date"
                                className="input"
                                value={newProject.endDate}
                                onChange={(e) => setNewProject((p) => ({...p, endDate: e.target.value}))}
                            />

                            <label>Skills (Komma getrennt)</label>
                            <input
                                className="input"
                                value={newProject.skillsText}
                                onChange={(e) => setNewProject((p) => ({...p, skillsText: e.target.value}))}
                                placeholder="React, C#, Postgres"
                            />

                            <label>Beschreibung</label>
                            <textarea
                                className="input"
                                value={newProject.description}
                                onChange={(e) => setNewProject((p) => ({...p, description: e.target.value}))}
                                style={{minHeight: 120}}
                            />

                            <label>Sortierung (kleiner = weiter oben)</label>
                            <input
                                type="number"
                                className="input"
                                value={newProject.sortOrder}
                                onChange={(e) => setNewProject((p) => ({...p, sortOrder: e.target.value}))}
                            />

                            <button type="button" onClick={handleCreateProject} className="button"
                                    style={{marginTop: 8}}>
                                Projekt anlegen
                            </button>
                        </div>

                        <div className="two-col-right">
                            <h4>Bestehende Projekte</h4>

                            <div className="working-time-list">
                                <table className="table">
                                    <thead>
                                    <tr>
                                        <th>Titel</th>
                                        <th>Zeitraum</th>
                                        <th>Skills</th>
                                        <th>Beschreibung</th>
                                        <th>Sort</th>
                                        <th>Aktion</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {projects.map((p) => {
                                        const period =
                                            (p.startDate ? formatDate(p.startDate) : "-") +
                                            " – " +
                                            (p.endDate ? formatDate(p.endDate) : "aktuell");

                                        const isEditing = editId === p.id;

                                        return (
                                            <tr key={p.id}>
                                                <td>
                                                    {isEditing ? (
                                                        <input
                                                            className="input"
                                                            value={editProject.title}
                                                            onChange={(e) =>
                                                                setEditProject((x) => ({...x, title: e.target.value}))
                                                            }
                                                        />
                                                    ) : (
                                                        p.title
                                                    )}
                                                </td>

                                                <td>
                                                    {isEditing ? (
                                                        <div style={{display: "flex", gap: 8}}>
                                                            <input
                                                                type="date"
                                                                className="input"
                                                                value={editProject.startDate}
                                                                onChange={(e) =>
                                                                    setEditProject((x) => ({
                                                                        ...x,
                                                                        startDate: e.target.value
                                                                    }))
                                                                }
                                                            />
                                                            <input
                                                                type="date"
                                                                className="input"
                                                                value={editProject.endDate}
                                                                onChange={(e) =>
                                                                    setEditProject((x) => ({
                                                                        ...x,
                                                                        endDate: e.target.value
                                                                    }))
                                                                }
                                                            />
                                                        </div>
                                                    ) : (
                                                        period
                                                    )}
                                                </td>

                                                <td>
                                                    {isEditing ? (
                                                        <input
                                                            className="input"
                                                            value={editProject.skillsText}
                                                            onChange={(e) =>
                                                                setEditProject((x) => ({
                                                                    ...x,
                                                                    skillsText: e.target.value
                                                                }))
                                                            }
                                                        />
                                                    ) : (
                                                        Array.isArray(p.skills) ? p.skills.join(", ") : "-"
                                                    )}
                                                </td>

                                                <td style={{maxWidth: 420}}>
                                                    {isEditing ? (
                                                        <textarea
                                                            className="input"
                                                            value={editProject.description}
                                                            onChange={(e) =>
                                                                setEditProject((x) => ({
                                                                    ...x,
                                                                    description: e.target.value
                                                                }))
                                                            }
                                                            style={{minHeight: 90}}
                                                        />
                                                    ) : (
                                                        p.description || "-"
                                                    )}
                                                </td>

                                                <td>
                                                    {isEditing ? (
                                                        <input
                                                            type="number"
                                                            className="input"
                                                            value={editProject.sortOrder}
                                                            onChange={(e) =>
                                                                setEditProject((x) => ({
                                                                    ...x,
                                                                    sortOrder: e.target.value
                                                                }))
                                                            }
                                                            style={{width: 90}}
                                                        />
                                                    ) : (
                                                        p.sortOrder ?? 0
                                                    )}
                                                </td>

                                                <td className="user-actions-row">
                                                    {isEditing ? (
                                                        <>
                                                            <button type="button" className="button" onClick={saveEdit}>
                                                                Speichern
                                                            </button>
                                                            <button type="button" className="button button-danger"
                                                                    onClick={cancelEdit}>
                                                                Abbrechen
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button type="button" className="button"
                                                                    onClick={() => startEdit(p)}>
                                                                Bearbeiten
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="button button-danger"
                                                                onClick={() => handleDeleteProject(p.id)}
                                                            >
                                                                Löschen
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {projects.length === 0 && (
                                        <tr>
                                            <td colSpan={6}>Keine Projekte gefunden.</td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>

                            <p style={{marginTop: 10, color: "#666"}}>
                                Hinweis: „Ende“ leer = „aktuell“.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}